import apiFetch from "@wordpress/api-fetch";
import { __ } from "@wordpress/i18n";

declare global {
	interface Window {
		wpApiSettings?: { nonce?: string; root?: string };
	}
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function validateEmail(email: string): string | null {
	if (!email.trim()) {
		return __("Email address is required.", "leat-crm");
	}
	if (!EMAIL_REGEX.test(email)) {
		return __("Please enter a valid email address.", "leat-crm");
	}
	return null;
}

type ApiLikeError = {
	code?: string;
	message?: string;
	data?: { scope?: "account" | "ip"; status?: number };
};

const FRIENDLY: Record<string, () => string> = {
	invalid_email: () => __("Please enter a valid email address.", "leat-crm"),
	invalid_credentials: () => __("Email or password is incorrect.", "leat-crm"),
};

export function extractApiError(err: unknown): string {
	// apiFetch surfaces network failures as TypeError ("Failed to fetch", etc.).
	// Other Error subclasses are real exceptions from our own code and shouldn't
	// be masked behind a generic network message.
	if (err instanceof TypeError) {
		return __(
			"Couldn't reach the server. Check your connection and try again.",
			"leat-crm",
		);
	}
	if (err instanceof Error) {
		return __("Something went wrong. Please try again.", "leat-crm");
	}
	const apiErr = err as ApiLikeError;

	if (apiErr?.code === "too_many_attempts") {
		if (apiErr.data?.scope === "account") {
			return __(
				"Too many failed attempts on this account. Please try again in an hour or reset your password.",
				"leat-crm",
			);
		}
		return __(
			"Too many sign-in attempts. Please wait a minute and try again.",
			"leat-crm",
		);
	}

	if (apiErr?.code && FRIENDLY[apiErr.code]) {
		return FRIENDLY[apiErr.code]();
	}

	if (apiErr?.message) {
		return apiErr.message;
	}

	return __("Something went wrong. Please try again.", "leat-crm");
}

export function handleApiErrorOrReload(err: unknown): string | null {
	const code = (err as { code?: string })?.code;
	if (code === "rest_cookie_invalid_nonce") {
		if (typeof window !== "undefined") window.location.reload();
		return null;
	}
	return extractApiError(err);
}

export function applyNewNonce(nonce: string): void {
	if (!nonce) return;
	if (typeof window !== "undefined") {
		window.wpApiSettings = { ...(window.wpApiSettings ?? {}), nonce };
	}
	const middleware = (
		apiFetch as unknown as { nonceMiddleware?: { nonce: string } }
	).nonceMiddleware;
	if (middleware) {
		middleware.nonce = nonce;
	}
}
